import {
  emptyPreviewEnvironmentLogTails,
  sendPreviewEnvironmentManagerStatus,
} from '../state.js';
import type { PreviewEnvironmentManagerRuntime } from '../runtime.js';
import type { PreparedPreviewEnvironmentStart } from './prepare-preview-environment-start.js';
import { attachStartedPreviewEnvironment } from './attach-started-preview-environment.js';
import { registerPreviewEnvironmentCapture } from './register-capture.js';
import { createBridgeTailBroadcastScheduler } from './schedule-bridge-tail-broadcast.js';
import {
  discardStalePreviewEnvironmentSpawn,
  spawnPreviewEnvironmentProcess,
} from './spawn-preview-environment-process.js';

export function runPreviewEnvironmentSpawnImmediate(
  runtime: PreviewEnvironmentManagerRuntime,
  environmentId: string,
  prepared: PreparedPreviewEnvironmentStart,
): void {
  const { state, getWs, log } = runtime;
  const { cwd, childEnv, cmd, title, scheduledGen, ac } = prepared;

  if ((state.spawnGenerationByServerId.get(environmentId) ?? 0) !== scheduledGen) {
    return;
  }

  let proc;
  let pipedStdoutStderr: boolean;
  let mergedLogPath: string | undefined;
  let mergedCleanupDir: string | undefined;
  try {
    const spawned = spawnPreviewEnvironmentProcess(cmd, childEnv, cwd, ac.signal);
    proc = spawned.proc;
    pipedStdoutStderr = spawned.pipedStdoutStderr;
    mergedLogPath = spawned.mergedLogPath;
    mergedCleanupDir = spawned.mergedCleanupDir;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    log(`[preview-environment] Failed to start ${title}: ${msg}`);
    state.abortControllersByServerId.delete(environmentId);
    sendPreviewEnvironmentManagerStatus(getWs, environmentId, 'error', msg, emptyPreviewEnvironmentLogTails());
    return;
  }

  if ((state.spawnGenerationByServerId.get(environmentId) ?? 0) !== scheduledGen) {
    discardStalePreviewEnvironmentSpawn(proc);
    return;
  }

  const { stdoutTail, stderrTail } = registerPreviewEnvironmentCapture(
    runtime,
    environmentId,
    title,
    pipedStdoutStderr,
    mergedLogPath,
    mergedCleanupDir,
  );

  attachStartedPreviewEnvironment({
    runtime,
    environmentId,
    title,
    proc,
    mergedLogPath,
    mergedCleanupDir,
    scheduledGen,
    stdoutTail,
    stderrTail,
    scheduleBridgeTailBroadcast: createBridgeTailBroadcastScheduler({
      state,
      getWs,
      environmentId,
      scheduledGen,
      proc,
      stdoutTail,
      stderrTail,
    }),
  });
}
